import { useMemo } from 'react';

import { useGetPatientCaseWithContentQuery } from '@/@generated/codegen/API';
import { safeParseCaseConfig, ValidationError } from '@/utils/types/validation';

import { useCasesConfig } from './useCasesConfig';

/**
 * Hook to load case data from GraphQL.
 *
 * @param caseId - The database ID of the case (not legacy key)
 * @param masterclassId - Optional masterclass ID to fetch case metadata
 * @returns Case configuration, assets base URL, loading state, and error
 */
export const useCaseData = (caseId: string, masterclassId?: string) => {
  const { getCaseMetadata } = useCasesConfig({ masterclassId });

  // Fetch case content from GraphQL
  const {
    data,
    loading: gqlLoading,
    error: gqlError,
  } = useGetPatientCaseWithContentQuery({
    variables: {
      input: { patientCaseId: caseId },
    },
    skip: !caseId,
    fetchPolicy: 'network-only',
  });

  // Get metadata for assets base URL
  const metadata = useMemo(() => getCaseMetadata(caseId), [caseId, getCaseMetadata]);
  const assetsBaseUrl = metadata?.assetsBaseUrl || '';

  // Parse and validate case config from GraphQL response
  const { caseConfig, error } = useMemo(() => {
    if (!data?.getPatientCase?.stringifiedContent) {
      return { caseConfig: null, error: null };
    }

    try {
      const parsedContent = JSON.parse(data.getPatientCase.stringifiedContent);

      // Validate with Zod
      const result = safeParseCaseConfig(parsedContent);

      if (!result.success) {
        console.error('[useCaseData] Validation errors:', result.error.errors);
        return {
          caseConfig: null,
          error: new ValidationError('Invalid case configuration', result.error),
        };
      }

      return { caseConfig: result.data, error: null };
    } catch (err) {
      console.error('[useCaseData] Error parsing case content:', err);
      return {
        caseConfig: null,
        error: err instanceof Error ? err : new Error('Failed to parse case content'),
      };
    }
  }, [data]);

  return {
    caseConfig,
    assetsBaseUrl,
    loading: gqlLoading,
    error: error || (gqlError?.message ? gqlError.message : null),
    getSlide: (slideId: string) => caseConfig?.slides.find((s) => s.id === slideId),
    getAsset: (assetId: string) => caseConfig?.assets.find((a) => a.id === assetId),
    getTotalSlides: () => caseConfig?.slides.length || 0,
  };
};
